import { useEffect, useState } from 'react';

const DEFAULT_PAGE_SIZE = 100;
const SEARCH_DELAY_MS = 300;

/**
 * Search, filter and page state for a table the backend pages.
 * The page and the search go to the API (`skip` / `limit` / `search`), so a search finds rows on
 * any page; the search is sent once typing pauses. Filters apply to the rows of the current page.
 * A new search, filter or page size goes back to page 1.
 */
export function useTableQuery<F extends string = 'all'>(initialFilter = 'all' as F) {
  const [searchTerm, setSearchTermState] = useState('');
  const [search, setSearch] = useState('');
  const [filter, setFilterState] = useState<F>(initialFilter);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSizeState] = useState(DEFAULT_PAGE_SIZE);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchTerm.trim()), SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const setSearchTerm = (value: string) => {
    setSearchTermState(value);
    setPage(1);
  };
  const setFilter = (value: F) => {
    setFilterState(value);
    setPage(1);
  };
  const setPageSize = (value: number) => {
    setPageSizeState(value);
    setPage(1);
  };

  return {
    searchTerm,
    /** The search the current rows were loaded with, lower case. */
    term: search.toLowerCase(),
    setSearchTerm,
    filter,
    setFilter,
    page,
    setPage,
    pageSize,
    setPageSize,
    pageQuery: { skip: (page - 1) * pageSize, limit: pageSize, search: search || undefined },
  };
}
